/** LIVE PLAYERS BOARD DESIGN — the owner designs the board themselves.
 *
 *  Logos, wallpaper, watermark, colours, fonts and the announcement banner,
 *  with a LIVE PREVIEW that changes as the controls do. Nothing is saved until
 *  Save is tapped, and "Reset to default" puts the board back to today's look
 *  in one tap. Everything is optional: empty means the board as it is now.
 *
 *  It is saved into the SAME gift setup the rest of Tap’N Win design uses
 *  (one `board_design` block), through the same /goal-game/admin/branding
 *  endpoint — there is no second design system.
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, RotateCcw, Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { FONT_CHOICES, TEXT_SIZES, loadFont } from '../../../lib/designTokens';
import { boardAbs, boardSkin } from '../../../lib/boardSkin';
import { BrandAssetPicker } from '../../voucher_settings/BrandAssetPicker';
import { getToken } from '../../../lib/session';
import { ImageField } from './ImageField';
import { btn } from './buttons';

const field = "w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-sm";

const Color = ({ label, value, onChange, testid }) => (
    <label className="block">
        <span className="block text-xs font-medium text-slate-500 mb-1">{label}</span>
        <div className="flex items-center gap-2">
            <input type="color" value={value || '#000000'} onChange={(e) => onChange(e.target.value)}
                   data-testid={testid}
                   className="h-9 w-12 rounded border border-slate-200 dark:border-slate-700 bg-transparent" />
            <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)}
                   placeholder="(board as it is today)" className={`${field} font-mono`} />
            {value && (
                <button type="button" onClick={() => onChange('')} data-testid={`${testid}-clear`}
                        className="shrink-0 text-xs text-slate-500 underline">clear</button>
            )}
        </div>
    </label>
);

const Pick = ({ label, value, options, onChange, testid }) => (
    <label className="block">
        <span className="block text-xs font-medium text-slate-500 mb-1">{label}</span>
        <select value={value || ''} onChange={(e) => onChange(e.target.value)}
                data-testid={testid} className={field}>
            {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
    </label>
);

const Slider = ({ label, value, min, max, onChange, testid }) => (
    <label className="block">
        <span className="block text-xs font-medium text-slate-500 mb-1">{label}</span>
        <input type="range" min={min} max={max} value={value}
               onChange={(e) => onChange(Number(e.target.value))}
               data-testid={testid} className="w-full" />
    </label>
);

/** The board as players will see it, drawn from the SAME resolver the real
 *  board uses — so this preview cannot drift from the live screen. */
const BoardPreview = ({ draft, fallbackLogo }) => {
    const skin = boardSkin({ board: draft }, {}, {}, 'en', fallbackLogo);
    const rows = [
        { player: 'Chloe H.', place: 'Molson Demo — Downtown', points: 24500 },
        { player: 'Sam G.', place: 'At home', points: 18500 },
    ];
    return (
        <div className="relative overflow-hidden rounded-2xl border border-slate-200 p-4 dark:border-slate-700"
             style={{ backgroundColor: skin.bg, color: skin.text || '#ffffff', ...skin.bodyStyle }}
             data-testid="gg-board-preview">
            {skin.wallpaperUrl && (
                <div className="pointer-events-none absolute inset-0 bg-cover bg-center"
                     style={{ backgroundImage: `url("${boardAbs(skin.wallpaperUrl)}")` }}>
                    <div className="absolute inset-0"
                         style={{ backgroundColor: skin.bg, opacity: skin.wallpaperOverlay / 100 }} />
                </div>
            )}
            {(skin.watermarkUrl || skin.watermarkText) && (
                <div className={`pointer-events-none absolute flex ${
                    skin.watermarkCorner ? 'bottom-3 right-3' : 'inset-0 items-center justify-center'}`}
                     style={{ opacity: skin.watermarkOpacity }}>
                    {skin.watermarkUrl
                        ? <img src={boardAbs(skin.watermarkUrl)} alt=""
                               style={{ width: `${skin.watermarkSize}%`, maxWidth: '80%' }}
                               className="object-contain" />
                        : <span className="font-black uppercase tracking-widest"
                                style={{ fontSize: `${Math.max(12, skin.watermarkSize / 2)}px` }}>
                            {skin.watermarkText}
                        </span>}
                </div>
            )}
            <div className="relative space-y-3">
                {/* The crowd band sits behind the logos only, the same as the
                    real board, and steps aside for a wallpaper. */}
                <div className="relative -mx-4 -mt-4 overflow-hidden px-4 pb-2 pt-4">
                    {!skin.wallpaperUrl && (
                        <div className="pointer-events-none absolute inset-0">
                            <div className="absolute inset-0 bg-cover bg-center opacity-60"
                                 style={{ backgroundImage: `url("${skin.headerImage}")` }}
                                 data-testid="gg-board-preview-crowd" />
                            <div className="absolute inset-0"
                                 style={{ backgroundImage:
                                     `linear-gradient(180deg, ${skin.bg}99 0%, ${skin.bg} 100%)` }} />
                        </div>
                    )}
                    <div className="relative flex min-h-[28px] items-center gap-2">
                        {skin.logoUrl && (
                            <img src={boardAbs(skin.logoUrl)} alt=""
                                 style={{ height: `${Math.min(skin.logoSize || 40, 60)}px` }}
                                 className="object-contain" />
                        )}
                        {skin.logo2Url && (
                            <img src={boardAbs(skin.logo2Url)} alt=""
                                 style={{ height: `${Math.min(skin.logo2Size || 28, 48)}px` }}
                                 className="object-contain" />
                        )}
                    </div>
                </div>

                {/* The pinned announcement, in its own colours. */}
                <div className="flex items-center gap-2 rounded-xl px-3 py-2"
                     style={{ backgroundColor: skin.bannerBg || skin.accent,
                              color: skin.bannerText || '#ffffff',
                              fontSize: skin.bannerSize ? undefined : '13px' }}
                     data-testid="gg-board-preview-banner">
                    {skin.bannerImage && (
                        <img src={boardAbs(skin.bannerImage)} alt="" className="h-6 object-contain" />
                    )}
                    <span className="font-bold">NEXT 10 MINS PITCHERS ARE $5</span>
                </div>

                <p className="text-base font-bold" style={skin.headlineStyle}>Live players</p>
                <div className="flex gap-1.5">
                    {['Players', 'Leaderboard', 'Chat'].map((label, i) => (
                        <span key={label}
                              className="rounded-full border px-3 py-1 text-[11px] font-semibold"
                              style={{ backgroundColor: i === 0 ? skin.accent : 'transparent',
                                       color: i === 0 ? '#ffffff' : skin.meta,
                                       borderColor: i === 0 ? skin.accent : skin.border }}>
                            {label}
                        </span>
                    ))}
                </div>
                {rows.map((r) => (
                    <div key={r.player} className="rounded-2xl border px-3 py-2"
                         style={{ backgroundColor: skin.card, borderColor: skin.border }}>
                        <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-bold">{r.player}</p>
                            <span className="rounded-lg border px-2 py-0.5 text-[11px] font-bold tabular-nums"
                                  style={{ color: skin.gold, borderColor: `${skin.gold}66` }}>
                                {r.points} pts
                            </span>
                        </div>
                        <p className="text-[11px]" style={{ color: skin.meta }}>{r.place}</p>
                        <p className="text-[11px] tabular-nums" style={{ color: skin.meta }}>
                            Points: {r.points}
                        </p>
                    </div>
                ))}
                {/* The panel's own bottom nav. */}
                <div className="-mx-4 -mb-4 flex items-center justify-between border-t px-6 pb-3 pt-2 text-[10px] font-semibold"
                     style={{ borderColor: skin.border, color: skin.meta }}>
                    {['Home', 'Leaderboard', 'Chat', 'Profile'].map((label, i) => (
                        <span key={label} style={i === 1 ? { color: skin.accent } : undefined}>
                            {label}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
};

export const GoalBoardDesignBlock = ({ cfg, orgId, promotionId, onReload }) => {
    const setupId = cfg.resolved_setup_id;
    const [draft, setDraft] = useState(null);
    const [busy, setBusy] = useState(false);
    const [failed, setFailed] = useState(false);
    const [picker, setPicker] = useState(null);

    const load = useCallback(async () => {
        if (!setupId) { setDraft(null); setFailed(true); return; }
        try {
            const res = await api.get(`/voucher-settings/gift-setups/${setupId}`,
                                      { params: { organization_id: orgId } });
            setDraft((res.data.config || {}).board_design || {});
            setFailed(false);
        } catch (_e) { setDraft(null); setFailed(true); }
    }, [setupId, orgId]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        loadFont(draft?.headline_font);
        loadFont(draft?.body_font);
    }, [draft]);

    const set = (k, v) => setDraft((p) => ({ ...(p || {}), [k]: v }));
    const setWord = (lang, v) => setDraft((p) => ({
        ...(p || {}), watermark_text: { ...((p || {}).watermark_text || {}), [lang]: v },
    }));

    const save = async (value) => {
        setBusy(true);
        try {
            await api.post('/goal-game/admin/branding', {
                promotion_id: promotionId || null,
                organization_id: orgId,
                setup_id: setupId,
                gift: { board_design: value === undefined ? draft : value },
            });
            await onReload?.();
            await load();
            toast.success('Live players board design saved');
        } catch (_e) { toast.error('Could not save the board design'); }
        finally { setBusy(false); }
    };

    if (!draft) {
        if (failed) {
            return (
                <p className="pt-3 text-sm text-slate-500" data-testid="gg-board-design-empty">
                    The board design could not be loaded — this organization may have no gift setup
                    yet. Open Branding, create one, and this card will load.
                </p>
            );
        }
        return <div className="pt-3"><Loader2 className="animate-spin text-slate-400" size={18} /></div>;
    }

    const words = draft.watermark_text || {};

    return (
        <div className="grid gap-4 pt-3 lg:grid-cols-[minmax(0,1fr)_300px]">
            <div className="min-w-0 space-y-5">
                <p className="text-xs text-slate-500">
                    Saved to the gift setup <strong>{cfg.resolved_setup_name}</strong>, like the rest of
                    Tap’N Win design. Everything is optional — empty means the board exactly as it
                    looks today, and nothing changes on any board until you save.
                </p>

                {/* 1 — logos */}
                <div className="space-y-3">
                    <p className="text-xs font-semibold text-slate-500">Logos</p>
                    <ImageField
                        label="Header logo" hint="Top of the board. Upload, or pick one from the library."
                        value={draft.logo_url} endpoint="board-asset" responseKey="image_url"
                        orgId={orgId} setupId={setupId} extraFields={{ slot: 'logo' }}
                        libraryKind="merchant_logo" libraryName="Board header logo"
                        onChange={(v) => set('logo_url', v)}
                        onLibrary={() => setPicker({ kind: 'merchant_logo',
                                                     apply: (url) => set('logo_url', url) })}
                        testid="gg-board-logo" />
                    <Slider label={`Header logo size — ${draft.logo_size_px || 40} px`}
                            value={draft.logo_size_px || 40} min={20} max={200}
                            onChange={(v) => set('logo_size_px', v)} testid="gg-board-logo-size" />
                    <ImageField
                        label="Second logo (optional)" hint="A sponsor's mark, beside the header logo."
                        value={draft.logo2_url} endpoint="board-asset" responseKey="image_url"
                        orgId={orgId} setupId={setupId} extraFields={{ slot: 'logo2' }}
                        libraryKind="merchant_logo" libraryName="Board second logo"
                        onChange={(v) => set('logo2_url', v)}
                        onLibrary={() => setPicker({ kind: 'merchant_logo',
                                                     apply: (url) => set('logo2_url', url) })}
                        testid="gg-board-logo2" />
                    <Slider label={`Second logo size — ${draft.logo2_size_px || 32} px`}
                            value={draft.logo2_size_px || 32} min={20} max={200}
                            onChange={(v) => set('logo2_size_px', v)} testid="gg-board-logo2-size" />
                </div>

                {/* 2 — wallpaper */}
                <div className="space-y-3">
                    <p className="text-xs font-semibold text-slate-500">Wallpaper</p>
                    <ImageField
                        label="Board wallpaper" hint="Behind the whole board."
                        value={draft.wallpaper_url} endpoint="board-asset" responseKey="image_url"
                        orgId={orgId} setupId={setupId} extraFields={{ slot: 'wallpaper' }}
                        libraryKind="wallpaper" libraryName="Board wallpaper"
                        onChange={(v) => set('wallpaper_url', v)}
                        onLibrary={() => setPicker({ kind: 'wallpaper',
                                                     apply: (url) => set('wallpaper_url', url) })}
                        testid="gg-board-wallpaper" />
                    <Slider label={`Wallpaper darkening — ${draft.wallpaper_overlay == null ? 45 : draft.wallpaper_overlay}% (keeps names readable)`}
                            value={draft.wallpaper_overlay == null ? 45 : draft.wallpaper_overlay}
                            min={0} max={90} onChange={(v) => set('wallpaper_overlay', v)}
                            testid="gg-board-overlay" />
                </div>

                {/* 3 — watermark */}
                <div className="space-y-3">
                    <p className="text-xs font-semibold text-slate-500">Watermark (behind the messages)</p>
                    <ImageField
                        label="Watermark image" hint="A logo shown faintly behind the board."
                        value={draft.watermark_url} endpoint="board-asset" responseKey="image_url"
                        orgId={orgId} setupId={setupId} extraFields={{ slot: 'watermark' }}
                        libraryKind="scratch_logo" libraryName="Board watermark"
                        onChange={(v) => set('watermark_url', v)}
                        onLibrary={() => setPicker({ kind: 'scratch_logo',
                                                     apply: (url) => set('watermark_url', url) })}
                        testid="gg-board-watermark" />
                    <div className="grid gap-3 sm:grid-cols-3">
                        {['en', 'fr', 'es'].map((lang) => (
                            <label key={lang} className="block">
                                <span className="block text-xs font-medium text-slate-500 mb-1">
                                    Watermark text ({lang.toUpperCase()})
                                </span>
                                <input value={words[lang] || ''} maxLength={40}
                                       onChange={(e) => setWord(lang, e.target.value)}
                                       data-testid={`gg-board-watermark-text-${lang}`}
                                       className={field} />
                            </label>
                        ))}
                    </div>
                    <div className="grid gap-3 sm:grid-cols-3">
                        <Pick label="Position" value={draft.watermark_position}
                              options={[{ key: '', label: 'Centre' }, { key: 'centre', label: 'Centre' },
                                        { key: 'corner', label: 'Corner' }]}
                              onChange={(v) => set('watermark_position', v)}
                              testid="gg-board-watermark-position" />
                        <Slider label={`Size — ${draft.watermark_size || 55}%`}
                                value={draft.watermark_size || 55} min={10} max={100}
                                onChange={(v) => set('watermark_size', v)}
                                testid="gg-board-watermark-size" />
                        <Slider label={`Opacity — ${draft.watermark_opacity == null ? 10 : draft.watermark_opacity}%`}
                                value={draft.watermark_opacity == null ? 10 : draft.watermark_opacity}
                                min={3} max={60} onChange={(v) => set('watermark_opacity', v)}
                                testid="gg-board-watermark-opacity" />
                    </div>
                </div>

                {/* 4 — colours */}
                <div className="space-y-3">
                    <p className="text-xs font-semibold text-slate-500">Colours</p>
                    <div className="grid gap-3 sm:grid-cols-2">
                        <Color label="Board background" value={draft.bg}
                               onChange={(v) => set('bg', v)} testid="gg-board-bg" />
                        <Color label="Message bubble" value={draft.bubble}
                               onChange={(v) => set('bubble', v)} testid="gg-board-bubble" />
                        <Color label="Text" value={draft.text}
                               onChange={(v) => set('text', v)} testid="gg-board-text" />
                        <Color label="Accent (tabs and Send)" value={draft.accent}
                               onChange={(v) => set('accent', v)} testid="gg-board-accent" />
                    </div>
                </div>

                {/* 5 — fonts and sizes */}
                <div className="grid gap-3 sm:grid-cols-2">
                    <Pick label="Headline font" value={draft.headline_font} options={FONT_CHOICES}
                          onChange={(v) => set('headline_font', v)} testid="gg-board-headline-font" />
                    <Pick label="Body font" value={draft.body_font} options={FONT_CHOICES}
                          onChange={(v) => set('body_font', v)} testid="gg-board-body-font" />
                    <Pick label="Headline size" value={draft.headline_size} options={TEXT_SIZES}
                          onChange={(v) => set('headline_size', v)} testid="gg-board-headline-size" />
                    <Pick label="Body text size" value={draft.body_size} options={TEXT_SIZES}
                          onChange={(v) => set('body_size', v)} testid="gg-board-body-size" />
                </div>

                {/* 6 — the pinned announcement's look */}
                <div className="space-y-3">
                    <p className="text-xs font-semibold text-slate-500">Announcement banner</p>
                    <div className="grid gap-3 sm:grid-cols-2">
                        <Color label="Banner colour" value={draft.banner_bg}
                               onChange={(v) => set('banner_bg', v)} testid="gg-board-banner-bg" />
                        <Color label="Banner text colour" value={draft.banner_text}
                               onChange={(v) => set('banner_text', v)} testid="gg-board-banner-text" />
                        <Pick label="Banner text size" value={draft.banner_text_size} options={TEXT_SIZES}
                              onChange={(v) => set('banner_text_size', v)}
                              testid="gg-board-banner-size" />
                    </div>
                    <ImageField
                        label="Banner image (optional)" hint="A small mark beside the announcement."
                        value={draft.banner_image_url} endpoint="board-asset" responseKey="image_url"
                        orgId={orgId} setupId={setupId} extraFields={{ slot: 'banner' }}
                        libraryKind="merchant_logo" libraryName="Announcement banner image"
                        onChange={(v) => set('banner_image_url', v)}
                        onLibrary={() => setPicker({ kind: 'merchant_logo',
                                                     apply: (url) => set('banner_image_url', url) })}
                        testid="gg-board-banner-image" />
                </div>

                <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => save()} disabled={busy}
                            data-testid="gg-board-design-save" className={btn.primary}>
                        {busy ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />}
                        Save board design
                    </button>
                    <button type="button" disabled={busy}
                            onClick={() => { setDraft({}); save({}); }}
                            data-testid="gg-board-design-reset" className={btn.secondary}>
                        <RotateCcw size={15} /> Reset to default
                    </button>
                </div>
            </div>

            {/* The live preview: every change lands here before anything is saved. */}
            <div className="lg:sticky lg:top-28">
                <p className="mb-2 text-xs font-semibold text-slate-500">Live preview</p>
                <BoardPreview draft={draft} fallbackLogo={cfg.logo_url} />
            </div>

            <BrandAssetPicker
                open={!!picker}
                onOpenChange={(v) => { if (!v) setPicker(null); }}
                kind={picker?.kind}
                orgId={orgId}
                token={getToken()}
                isSuperAdmin
                onApply={(url, asset) => { picker?.apply?.(url, asset); setPicker(null); }}
            />
        </div>
    );
};

export default GoalBoardDesignBlock;
